import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import hamburgerMenu from "../../../assets/mobile_hamburger.svg";
import closeMenu from "../../../assets/mobile_close.svg";

import { NavLink } from "./NavLink";
import { LangSelector } from "./LangSelector";
import { RootState } from "../../../store/store";
import { logout } from "../../../store/slices/UserSlice";
import { setShowFeedBack } from "../../../store/slices/MainScreenSlice";

export const Header = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();
  const authenticated = useSelector(
    (state: RootState) => state.user.authenticated,
  );
  const user = useSelector((state: RootState) => state.user.user);

  const navLinks = [
    { label: t("new_navbar.how_to_play"), href: "#how-to-play" },
    { label: t("new_navbar.blog"), href: "/blog" },
    { label: t("new_navbar.about"), href: "#about" },
  ];

  // Hysteresis avoids rapid on/off at one threshold (elastic scroll, subpixel layout).
  const SCROLL_ON = 20;
  const SCROLL_OFF = 4;

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setIsScrolled((prev) => (prev ? y > SCROLL_OFF : y > SCROLL_ON));
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isUserMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isUserMenuOpen]);

  const scrollToSection = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    if (!href.startsWith("#")) return;

    e.preventDefault();
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
    }
    setIsMenuOpen(false);
  };

  return (
    <header
      data-testid="landing-header"
      className={`
        fixed top-0 left-0 right-0 z-50 [transform:translateZ(0)] [backface-visibility:hidden]
        transition-[background-color,box-shadow] duration-300
        ${isScrolled ? "bg-hero-orange shadow-md" : "bg-transparent"}
      `}
    >
      <div className="relative px-4 py-4 mx-auto">
        <div className="flex items-center justify-between">
          {/* Desktop Navigation */}
          <nav className="items-center hidden gap-8 overflow-visible lg:flex">
            {!authenticated && (
              <NavLink
                href="/login"
                isScrolled={isScrolled}
                onClick={(e) => scrollToSection(e, "/login")}
              >
                {t("new_navbar.signin")}
              </NavLink>
            )}

            {authenticated && (
              <div className="relative" ref={userMenuRef}>
                <button
                  type="button"
                  className={`font-bold ${
                    isScrolled ? "text-burgundy" : "text-white"
                  }`}
                  onClick={() => setIsUserMenuOpen((prev) => !prev)}
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="menu"
                >
                  {user?.name} {user?.familyName}
                </button>
                {isUserMenuOpen && (
                  <div className="absolute start-0 z-50 mt-2 flex min-w-[180px] flex-col whitespace-nowrap rounded-md bg-white shadow-lg">
                    <button
                      type="button"
                      className="px-4 py-2 text-start text-sm font-semibold text-burgundy hover:bg-hero-orange/10"
                      onClick={() => {
                        dispatch(setShowFeedBack(true));
                        setIsUserMenuOpen(false);
                      }}
                    >
                      {t("navbar.share")}
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 text-start text-sm font-semibold text-burgundy hover:bg-hero-orange/10"
                      onClick={() => {
                        dispatch(logout({}));
                        setIsUserMenuOpen(false);
                      }}
                    >
                      {t("navbar.logout")}
                    </button>
                  </div>
                )}
              </div>
            )}

            {navLinks.map((link) => (
              <NavLink
                key={link.href}
                href={link.href}
                isScrolled={isScrolled}
                onClick={(e) => scrollToSection(e, link.href)}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* Language Selector */}
          <LangSelector />

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`p-2 lg:hidden transition-colors ${
              isScrolled ? "text-burgundy" : "text-white"
            }`}
            aria-label="Toggle menu"
          >
            <img src={isMenuOpen ? closeMenu : hamburgerMenu} alt="Menu" />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 top-[72px] bg-black/40 lg:hidden"
              onClick={() => setIsMenuOpen(false)}
            />

            {/* Menu Panel */}
            <nav className="absolute left-0 right-0 top-full lg:hidden bg-black/80 backdrop-blur-lg animate-menu-slide-down">
              <div className="flex flex-col gap-4 px-4 py-6">
                {!authenticated && (
                  <a
                    href="/login"
                    onClick={() => setIsMenuOpen(false)}
                    className="text-lg font-bold text-burgundy transition-colors bg-white text-center p-2 rounded-lg w-max mx-auto"
                  >
                    {t("new_navbar.signin")}
                  </a>
                )}

                {authenticated && (
                  <>
                    <span className="text-lg font-bold text-white text-center">
                      {user?.name} {user?.familyName}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        dispatch(setShowFeedBack(true));
                        setIsMenuOpen(false);
                      }}
                      className="text-lg font-bold text-burgundy transition-colors bg-white text-center p-2 rounded-lg w-max mx-auto"
                    >
                      {t("navbar.share")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        dispatch(logout({}));
                        setIsMenuOpen(false);
                      }}
                      className="text-lg font-bold text-burgundy transition-colors bg-white text-center p-2 rounded-lg w-max mx-auto"
                    >
                      {t("navbar.logout")}
                    </button>
                  </>
                )}

                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-lg font-bold text-burgundy transition-colors bg-white text-center p-2 rounded-lg w-max mx-auto"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </nav>
          </>
        )}
      </div>
    </header>
  );
};
